import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import {
  RiEditLine,
  RiDeleteBinLine,
  RiAddLine,
  RiArrowLeftSLine,
  RiArrowRightSLine,
  RiCloseLine,
} from "react-icons/ri";
import toast from "react-hot-toast";
import uploadToCloudinary from "../../utils/cloudinary";
import { confirmAndDelete } from "../../utils/confirmAndDelete";
const BlogCardSkeleton = () => (
  <div className="bg-white rounded-2xl overflow-hidden shadow-xl border border-gray-200 animate-pulse">
    <div className="h-48 bg-gray-200" />
    <div className="p-6 space-y-4">
      <div className="h-5 bg-gray-200 rounded w-3/4"></div>
      <div className="h-4 bg-gray-200 rounded w-full"></div>
      <div className="h-4 bg-gray-200 rounded w-5/6"></div>
      <div className="flex gap-2">
        <div className="h-6 w-12 bg-gray-200 rounded"></div>
        <div className="h-6 w-16 bg-gray-200 rounded"></div>
      </div>
    </div>
  </div>
);

const NewsBlogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    images: [""],
    tags: "",
    status: "",
    author: "",
  });
  const blogsPerPage = 2;

  // Fetch blogs from API
  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/news-blogs/all-news-blogs`
      );
      setBlogs(res.data.newsBlogs);
    } catch (error) {
      console.error("Error fetching blogs:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  // Pagination logic
  const indexOfLastBlog = currentPage * blogsPerPage;
  const indexOfFirstBlog = indexOfLastBlog - blogsPerPage;
  const currentBlogs = blogs.slice(indexOfFirstBlog, indexOfLastBlog);
  const totalPages = Math.ceil(blogs.length / blogsPerPage);

  // Form handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const addImageField = () => {
    setFormData({ ...formData, images: [...formData.images, ""] });
  };

  const removeImageField = (index) => {
    if (formData.images.length > 1) {
      const newImages = [...formData.images];
      newImages.splice(index, 1);
      setFormData({ ...formData, images: newImages });
    }
  };

  const openNewBlogModal = () => {
    setEditingBlog(null);
    setFormData({
      title: "",
      content: "",
      images: [""],
      tags: "",
      author: "",
      status: "published",
    });
    setIsModalOpen(true);
  };

  const openEditBlogModal = (blog) => {
    setEditingBlog(blog);
    setFormData({
      title: blog.title,
      content: blog.content,
      images: [...blog.images],
      tags: blog.tags.join(", "),
      author: blog.author,
      status: blog.status,
    });
    setIsModalOpen(true);
  };

  // Handle create/update
  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      title: formData.title,
      content: formData.content,
      images: formData.images.filter((img) => img !== ""),
      tags: formData.tags
        .split(",")
        .map((t) => t.trim())
        .filter((t) => t),
      author: formData.author,
      status: formData.status,
    };

    try {
      setIsSubmitting(true);
      if (editingBlog) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/news-blogs/edit-news-blog/${editingBlog._id}`,
          payload
        );
        toast.success("Blog updated successfully");
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/news-blogs/create-news-blog`,
          payload
        );
        toast.success("New blog created ");
      }
      fetchBlogs();
      setIsModalOpen(false);
    } catch (err) {
      console.error("Error saving blog:", err);
      toast.error("Something went wrong ");
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteBlog = async (id) => {
    const isConfirmed = await confirmAndDelete({
      id,
      url: `${process.env.REACT_APP_API_URL}/news-blogs/delete-news-blog`,
    });

    if (isConfirmed) {
      try {
        // Refresh blogs
        await fetchBlogs();

        // Check if current page is now empty
        const newTotalPages = Math.ceil((blogs.length - 1) / blogsPerPage);
        if (currentPage > newTotalPages) {
          setCurrentPage(newTotalPages || 1);
        }

        Swal.fire({
          title: "Deleted!",
          text: "The blog has been deleted.",
          icon: "success",
          confirmButtonColor: "#10B981",
        });
      } catch (err) {
        console.error("Error deleting blog:", err);
        Swal.fire({
          title: "Error!",
          text: "Something went wrong.",
          icon: "error",
          confirmButtonColor: "#EF4444",
        });
      }
    }
  };

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Upload a single image to Cloudinary
  const handleFileUpload = async (file, index) => {
    if (!file) return;

    const toastId = toast.loading("Uploading image...");
    try {
      const uploadedUrl = await uploadToCloudinary(file);

      // Replace image at the right index
      const newImages = [...formData.images];
      newImages[index] = uploadedUrl;
      setFormData({ ...formData, images: newImages });

      toast.success("Image uploaded successfully!", { id: toastId });
    } catch (err) {
      console.error("Cloudinary upload failed:", err);
      toast.error("Image upload failed", { id: toastId });
    }
  };

  return (
    <div className="min-h-screen bg-transparent p-6">
      <div>
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-heading font-bold bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor bg-clip-text text-transparent">
            News & Blogs
          </h1>
          <button
            onClick={openNewBlogModal}
            className="bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor text-white py-2 px-4 rounded-xl flex items-center shadow-lg hover:shadow-xl transition-shadow"
          >
            <RiAddLine className="mr-2" />
            New Post
          </button>
        </div>

        {/* Blog List */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {loading
            ? Array.from({ length: blogsPerPage }).map((_, i) => (
                <BlogCardSkeleton key={i} />
              ))
            : currentBlogs.map((blog) => (
                <div
                  key={blog._id}
                  className="bg-white rounded-2xl overflow-hidden shadow-xl border border-primaryTextColor transition-transform hover:scale-[1.02]"
                >
                  <div className="h-48 overflow-hidden">
                    <img
                      src={blog.images[0]}
                      alt={blog.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <h2 className="text-xl font-bold text-primaryTextColor font-heading">
                        {blog.title}
                      </h2>

                      <div className="flex flex-col items-end">
                        {/* <span className="text-xs text-primaryBtnHoverColor bg-primaryBtnColor bg-opacity-20 px-2 py-1 rounded-full">
                          {formatDate(blog.createdAt)}
                        </span> */}
                        {/* Status Badge */}
                        <span
                          className={`mt-1 text-xs px-2 py-1 rounded-full ${
                            blog.status === "published"
                              ? "bg-green-200 text-green-800"
                              : blog.status === "coming-soon"
                              ? "bg-yellow-200 text-yellow-800"
                              : "bg-gray-200 text-gray-800"
                          }`}
                        >
                          {blog.status.charAt(0).toUpperCase() +
                            blog.status.slice(1).replace("-", " ")}
                        </span>
                      </div>
                    </div>
                    <p className="text-secondaryTextColor min-h-12  mb-4 line-clamp-2">
                      {blog.content}
                    </p>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {blog.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="text-xs text-primaryBtnHoverColor bg-primaryBtnColor bg-opacity-20 px-2 py-1 rounded-full"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                    <div className="flex justify-between items-center">
                      <p className="text-sm text-gray-500">By {blog.author}</p>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => openEditBlogModal(blog)}
                          className="p-2 bg-gray-100 rounded-lg text-primaryBtnHoverColor hover:bg-primaryBtnColor hover:text-white transition-colors"
                        >
                          <RiEditLine />
                        </button>
                        <button
                          onClick={() => deleteBlog(blog._id)}
                          className="p-2 bg-gray-100 rounded-lg text-red-500 hover:bg-red-500 hover:text-white transition-colors"
                        >
                          <RiDeleteBinLine />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center space-x-4 mt-8">
            <button
              onClick={() => currentPage > 1 && paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className={`p-2 rounded-lg ${
                currentPage === 1
                  ? "bg-primaryTextColor text-gray-500 cursor-not-allowed"
                  : "bg-primaryBtnColor text-white hover:bg-primaryBtnHoverColor"
              }`}
            >
              <RiArrowLeftSLine size={20} />
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => paginate(page)}
                className={`px-3 py-1 rounded-lg ${
                  currentPage === page
                    ? "bg-primaryBtnColor text-white"
                    : "bg-primaryTextColor text-gray-300 hover:bg-secondaryTextColor"
                }`}
              >
                {page}
              </button>
            ))}

            <button
              onClick={() =>
                currentPage < totalPages && paginate(currentPage + 1)
              }
              disabled={currentPage === totalPages}
              className={`p-2 rounded-lg ${
                currentPage === totalPages
                  ? "bg-primaryTextColor text-gray-500 cursor-not-allowed"
                  : "bg-primaryBtnColor text-white hover:bg-primaryBtnHoverColor"
              }`}
            >
              <RiArrowRightSLine size={20} />
            </button>
          </div>
        )}

        {/* Add/Edit Blog Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-gray-300 shadow-xl">
              <div className="sticky top-0 bg-white p-4 border-b border-gray-300 flex justify-between items-center">
                <h2 className="text-xl font-bold text-primaryTextColor">
                  {editingBlog ? "Edit Blog Post" : "Create New Blog Post"}
                </h2>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-gray-500 hover:text-primaryBtnHoverColor"
                >
                  <RiCloseLine size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6">
                <div className="mb-4">
                  <label className="block text-gray-700 mb-2">Title</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="w-full bg-white border border-gray-300 rounded-lg p-3 text-primaryTextColor"
                    required
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 mb-2">Content</label>
                  <textarea
                    name="content"
                    value={formData.content}
                    onChange={handleInputChange}
                    rows="5"
                    className="w-full bg-white border border-gray-300 rounded-lg p-3 text-primaryTextColor"
                    required
                  />
                </div>

                {/* Image upload */}
                <div className="mb-4">
                  <label className="block text-gray-700 mb-2">Images</label>
                  {formData.images.map((url, index) => (
                    <div key={index} className="flex items-center mb-2">
                      {url && (
                        <img
                          src={url}
                          alt={`preview-${index}`}
                          className="w-20 h-20 object-cover rounded-lg mr-2"
                        />
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) =>
                          handleFileUpload(e.target.files[0], index)
                        }
                        className="flex-1 border border-gray-300 rounded-lg p-2 text-primaryTextColor"
                      />
                      {formData.images.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeImageField(index)}
                          className="bg-red-500 text-white p-2 rounded-lg ml-2 hover:bg-red-600"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addImageField}
                    className="bg-white text-primaryBtnColor border border-gray-300 p-2 rounded-lg hover:bg-gray-100 mt-2"
                  >
                    Add Another Image
                  </button>
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 mb-2">
                    Tags (comma separated)
                  </label>
                  <input
                    type="text"
                    name="tags"
                    value={formData.tags}
                    onChange={handleInputChange}
                    className="w-full bg-white border border-gray-300 rounded-lg p-3 text-primaryTextColor"
                  />
                </div>

                <div className="mb-6">
                  <label className="block text-gray-700 mb-2">Author</label>
                  <input
                    type="text"
                    name="author"
                    value={formData.author}
                    onChange={handleInputChange}
                    className="w-full bg-white border border-gray-300 rounded-lg p-3 text-primaryTextColor"
                    required
                  />
                </div>
                <div className="mb-6">
                  <label className="block text-gray-700 mb-2">Status</label>
                  <select
                    name="status"
                    value={formData.status || "published"}
                    onChange={handleInputChange}
                    className="w-full bg-white border border-gray-300 rounded-lg p-3 text-primaryTextColor"
                    required
                  >
                    <option value="published">Published</option>
                    <option value="coming-soon">Coming Soon</option>
                    <option value="draft">Draft</option>
                  </select>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="bg-gray-100 text-primaryTextColor py-2 px-4 rounded-lg hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-primaryBtnColor text-white w-36 flex items-center justify-center py-2 px-4 rounded-lg hover:bg-primaryBtnHoverColor"
                  >
                    {isSubmitting ? (
                      <div class="spinner w-full"></div>
                    ) : editingBlog ? (
                      "Update Post"
                    ) : (
                      "Create Post"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewsBlogs;

// import React, { useState } from "react";
// import {
//   RiEditLine,
//   RiDeleteBinLine,
//   RiAddLine,
//   RiArrowLeftSLine,
//   RiArrowRightSLine,
//   RiCloseLine,
// } from "react-icons/ri";

// const NewsBlogs = () => {
//   // Sample initial data
//   const initialBlogs = [
//     {
//       id: 1,
//       title: "Upcoming Inter-Academy Football Tournament",
//       content:
//         "We are excited to announce our annual Inter-Academy Football Tournament! Teams from across the region will compete at our stadium. Matches will begin on October 15, and the finals are scheduled for October 20.",
//       images: [
//         "https://st2.depositphotos.com/4318427/6334/i/450/depositphotos_63345113-stock-photo-football-match-for-children-training.jpg",
//         "https://st2.depositphotos.com/4318427/11021/i/950/depositphotos_110215036-stock-photo-football-match-for-children-training.jpg",
//       ],
//       tags: ["football", "tournament", "academy events"],
//       author: "Sports Academy Admin",
//       date: "2023-09-28",
//     },
//     {
//       id: 2,
//       title: "New Training Facilities Now Open",
//       content:
//         "Our state-of-the-art training facilities are now open for all academy members. Featuring the latest equipment and technology to help athletes reach their full potential.",
//       images: [
//         "https://cdn.prod.website-files.com/57822c659e1627a433e6a7c6/6502e93bbb7def28d7b9ee91_TfrKooMxB0WeDlJItgJJQDv75H6C7a4hxG75bOqRqAvmSgv3AyZTZFyTwXgyfURRZy8ntObmaMxAyPwWIhZaUru-m0NE6_cVlm0plvmx2km-UfPrw3D_FxFet2fYUkqODGX9uLEL2eKUkCJSwn0ojA.jpeg",
//       ],
//       tags: ["facilities", "training", "announcement"],
//       author: "Facilities Manager",
//       date: "2023-09-25",
//     },
//     {
//       id: 3,
//       title: "Basketball Tryouts Next Week",
//       content:
//         "Tryouts for the academy basketball team will be held next week. All interested players should register online before Friday.",
//       images: [
//         "https://static1.gensler.com/uploads/image/76310/LAFC_hero-1024_1656699354.jpg",
//       ],
//       tags: ["basketball", "tryouts", "teams"],
//       author: "Coach Williams",
//       date: "2023-09-20",
//     },
//   ];

//   // State management
//   const [blogs, setBlogs] = useState(initialBlogs);
//   const [currentPage, setCurrentPage] = useState(1);
//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [editingBlog, setEditingBlog] = useState(null);
//   const [formData, setFormData] = useState({
//     title: "",
//     content: "",
//     images: [""],
//     tags: "",
//     author: "",
//   });
//   const blogsPerPage = 2;

//   // Pagination logic
//   const indexOfLastBlog = currentPage * blogsPerPage;
//   const indexOfFirstBlog = indexOfLastBlog - blogsPerPage;
//   const currentBlogs = blogs.slice(indexOfFirstBlog, indexOfLastBlog);
//   const totalPages = Math.ceil(blogs.length / blogsPerPage);

//   // Handle form input changes
//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setFormData({
//       ...formData,
//       [name]: value,
//     });
//   };

//   // Handle image URL changes
//   const handleImageChange = (index, value) => {
//     const newImages = [...formData.images];
//     newImages[index] = value;
//     setFormData({
//       ...formData,
//       images: newImages,
//     });
//   };

//   // Add new image field
//   const addImageField = () => {
//     setFormData({
//       ...formData,
//       images: [...formData.images, ""],
//     });
//   };

//   // Remove image field
//   const removeImageField = (index) => {
//     if (formData.images.length > 1) {
//       const newImages = [...formData.images];
//       newImages.splice(index, 1);
//       setFormData({
//         ...formData,
//         images: newImages,
//       });
//     }
//   };

//   // Open modal for new blog
//   const openNewBlogModal = () => {
//     setEditingBlog(null);
//     setFormData({
//       title: "",
//       content: "",
//       images: [""],
//       tags: "",
//       author: "",
//     });
//     setIsModalOpen(true);
//   };

//   // Open modal for editing blog
//   const openEditBlogModal = (blog) => {
//     setEditingBlog(blog);
//     setFormData({
//       title: blog.title,
//       content: blog.content,
//       images: [...blog.images],
//       tags: blog.tags.join(", "),
//       author: blog.author,
//     });
//     setIsModalOpen(true);
//   };

//   // Handle form submission
//   const handleSubmit = (e) => {
//     e.preventDefault();

//     const newBlog = {
//       id: editingBlog ? editingBlog.id : Date.now(),
//       title: formData.title,
//       content: formData.content,
//       images: formData.images.filter((url) => url.trim() !== ""),
//       tags: formData.tags
//         .split(",")
//         .map((tag) => tag.trim())
//         .filter((tag) => tag !== ""),
//       author: formData.author,
//       date: editingBlog
//         ? editingBlog.date
//         : new Date().toISOString().split("T")[0],
//     };

//     if (editingBlog) {
//       // Update existing blog
//       setBlogs(
//         blogs.map((blog) => (blog.id === editingBlog.id ? newBlog : blog))
//       );
//     } else {
//       // Add new blog
//       setBlogs([newBlog, ...blogs]);
//     }

//     setIsModalOpen(false);
//   };

//   // Delete a blog
//   const deleteBlog = (id) => {
//     if (window.confirm("Are you sure you want to delete this blog post?")) {
//       setBlogs(blogs.filter((blog) => blog.id !== id));
//     }
//   };

//   // Change page
//   const paginate = (pageNumber) => setCurrentPage(pageNumber);

//   // Format date
//   const formatDate = (dateString) => {
//     const options = { year: "numeric", month: "short", day: "numeric" };
//     return new Date(dateString).toLocaleDateString(undefined, options);
//   };

//   return (
//     <div className="min-h-screen bg-transparent p-6">
//       <div className="">
//         {/* Header */}
//         <div className="flex justify-between items-center mb-8">
//           <h1 className="text-3xl font-heading font-bold bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor bg-clip-text text-transparent">
//             News & Blogs
//           </h1>
//           <button
//             onClick={openNewBlogModal}
//             className="bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor text-white py-2 px-4 rounded-xl flex items-center shadow-lg hover:shadow-xl transition-shadow"
//           >
//             <RiAddLine className="mr-2" />
//             New Post
//           </button>
//         </div>

//         {/* Blog List */}
//         <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
//           {currentBlogs.map((blog) => (
//             <div
//               key={blog.id}
//               className="bg-white rounded-2xl overflow-hidden shadow-xl border border-primaryTextColor transition-transform hover:scale-[1.02]"
//             >
//               {/* Blog Image */}
//               <div className="h-48 overflow-hidden">
//                 <img
//                   src={blog.images[0]}
//                   alt={blog.title}
//                   className="w-full h-full object-cover"
//                 />
//               </div>

//               {/* Blog Content */}
//               <div className="p-6">
//                 <div className="flex justify-between items-start mb-4">
//                   <h2 className="text-xl font-bold text-primaryTextColor font-heading">
//                     {blog.title}
//                   </h2>
//                   <span className="text-xs text-primaryBtnHoverColor bg-primaryBtnColor bg-opacity-20 px-2 py-1 rounded-full">
//                     {formatDate(blog.date)}
//                   </span>
//                 </div>

//                 <p className="text-secondaryTextColor mb-4 line-clamp-3">
//                   {blog.content}
//                 </p>

//                 {/* Tags */}
//                 <div className="flex flex-wrap gap-2 mb-4">
//                   {blog.tags.map((tag, index) => (
//                     <span
//                       key={index}
//                       className="text-xs text-primaryBtnHoverColor bg-primaryBtnColor bg-opacity-20 px-2 py-1 rounded-full"
//                     >
//                       #{tag}
//                     </span>
//                   ))}
//                 </div>

//                 {/* Author and Actions */}
//                 <div className="flex justify-between items-center">
//                   <p className="text-sm text-gray-500">By {blog.author}</p>
//                   <div className="flex space-x-2">
//                     <button
//                       onClick={() => openEditBlogModal(blog)}
//                       className="p-2 bg-gray-100 rounded-lg text-primaryBtnHoverColor hover:bg-primaryBtnColor hover:text-white transition-colors"
//                     >
//                       <RiEditLine />
//                     </button>
//                     <button
//                       onClick={() => deleteBlog(blog.id)}
//                       className="p-2 bg-gray-100 rounded-lg text-red-500 hover:bg-red-500 hover:text-white transition-colors"
//                     >
//                       <RiDeleteBinLine />
//                     </button>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           ))}
//         </div>

//         {/* Pagination */}
//         {totalPages > 1 && (
//           <div className="flex justify-center items-center space-x-4 mt-8">
//             <button
//               onClick={() => currentPage > 1 && paginate(currentPage - 1)}
//               disabled={currentPage === 1}
//               className={`p-2 rounded-lg ${
//                 currentPage === 1
//                   ? "bg-primaryTextColor text-gray-500 cursor-not-allowed"
//                   : "bg-primaryBtnColor text-white hover:bg-primaryBtnHoverColor"
//               }`}
//             >
//               <RiArrowLeftSLine size={20} />
//             </button>

//             {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
//               <button
//                 key={page}
//                 onClick={() => paginate(page)}
//                 className={`px-3 py-1 rounded-lg ${
//                   currentPage === page
//                     ? "bg-primaryBtnColor text-white"
//                     : "bg-primaryTextColor text-gray-300 hover:bg-secondaryTextColor"
//                 }`}
//               >
//                 {page}
//               </button>
//             ))}

//             <button
//               onClick={() =>
//                 currentPage < totalPages && paginate(currentPage + 1)
//               }
//               disabled={currentPage === totalPages}
//               className={`p-2 rounded-lg ${
//                 currentPage === totalPages
//                   ? "bg-primaryTextColor text-gray-500 cursor-not-allowed"
//                   : "bg-primaryBtnColor text-white hover:bg-primaryBtnHoverColor"
//               }`}
//             >
//               <RiArrowRightSLine size={20} />
//             </button>
//           </div>
//         )}

//         {/* Add/Edit Blog Modal */}
//         {isModalOpen && (
//           <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
//             <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-gray-300 shadow-xl">
//               <div className="sticky top-0 bg-white p-4 border-b border-gray-300 flex justify-between items-center">
//                 <h2 className="text-xl font-bold text-primaryTextColor">
//                   {editingBlog ? "Edit Blog Post" : "Create New Blog Post"}
//                 </h2>
//                 <button
//                   onClick={() => setIsModalOpen(false)}
//                   className="text-gray-500 hover:text-primaryBtnHoverColor"
//                 >
//                   <RiCloseLine size={24} />
//                 </button>
//               </div>

//               <form onSubmit={handleSubmit} className="p-6">
//                 <div className="mb-4">
//                   <label className="block text-gray-700 mb-2">Title</label>
//                   <input
//                     type="text"
//                     name="title"
//                     value={formData.title}
//                     onChange={handleInputChange}
//                     className="w-full bg-white border border-gray-300 rounded-lg p-3 text-primaryTextColor"
//                     required
//                   />
//                 </div>

//                 <div className="mb-4">
//                   <label className="block text-gray-700 mb-2">Content</label>
//                   <textarea
//                     name="content"
//                     value={formData.content}
//                     onChange={handleInputChange}
//                     rows="5"
//                     className="w-full bg-white border border-gray-300 rounded-lg p-3 text-primaryTextColor"
//                     required
//                   />
//                 </div>

//                 <div className="mb-4">
//                   <label className="block text-gray-700 mb-2">Image URLs</label>
//                   {formData.images.map((url, index) => (
//                     <div key={index} className="flex mb-2">
//                       <input
//                         type="url"
//                         value={url}
//                         onChange={(e) =>
//                           handleImageChange(index, e.target.value)
//                         }
//                         className="flex-1 bg-white border border-gray-300 rounded-lg p-3 text-primaryTextColor mr-2"
//                         placeholder="https://example.com/image.jpg"
//                       />
//                       {formData.images.length > 1 && (
//                         <button
//                           type="button"
//                           onClick={() => removeImageField(index)}
//                           className="bg-red-500 text-white p-3 rounded-lg hover:bg-red-600"
//                         >
//                           Remove
//                         </button>
//                       )}
//                     </div>
//                   ))}
//                   <button
//                     type="button"
//                     onClick={addImageField}
//                     className="bg-white text-primaryBtnColor border border-gray-300 p-2 rounded-lg hover:bg-gray-100 mt-2"
//                   >
//                     Add Another Image
//                   </button>
//                 </div>

//                 <div className="mb-4">
//                   <label className="block text-gray-700 mb-2">
//                     Tags (comma separated)
//                   </label>
//                   <input
//                     type="text"
//                     name="tags"
//                     value={formData.tags}
//                     onChange={handleInputChange}
//                     className="w-full bg-white border border-gray-300 rounded-lg p-3 text-primaryTextColor"
//                   />
//                 </div>

//                 <div className="mb-6">
//                   <label className="block text-gray-700 mb-2">Author</label>
//                   <input
//                     type="text"
//                     name="author"
//                     value={formData.author}
//                     onChange={handleInputChange}
//                     className="w-full bg-white border border-gray-300 rounded-lg p-3 text-primaryTextColor"
//                     required
//                   />
//                 </div>

//                 <div className="flex justify-end space-x-3">
//                   <button
//                     type="button"
//                     onClick={() => setIsModalOpen(false)}
//                     className="bg-gray-100 text-primaryTextColor py-2 px-4 rounded-lg hover:bg-gray-200"
//                   >
//                     Cancel
//                   </button>
//                   <button
//                     type="submit"
//                     className="bg-primaryBtnColor text-white py-2 px-4 rounded-lg hover:bg-primaryBtnHoverColor"
//                   >
//                     {editingBlog ? "Update Post" : "Create Post"}
//                   </button>
//                 </div>
//               </form>
//             </div>
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// export default NewsBlogs;
